import React, { Children, cloneElement, useEffect } from 'react';

function joinClassNames(...values) {
  return values.filter(Boolean).join(' ');
}

export function FormGroup({ className, children, ...rest }) {
  return (
    <div className={joinClassNames('form-group', className)} {...rest}>
      {children}
    </div>
  );
}

export function ControlLabel({ className, children, ...rest }) {
  return (
    <label className={joinClassNames('control-label', className)} {...rest}>
      {children}
    </label>
  );
}

export function FormControl({ componentClass, className, children, ...rest }) {
  if (componentClass === 'select') {
    return (
      <select className={joinClassNames('form-control', className)} {...rest}>
        {children}
      </select>
    );
  }

  return <input className={joinClassNames('form-control', className)} {...rest} />;
}

export function Col({ sm, className, children, ...rest }) {
  const gridClass = sm ? `col-sm-${sm}` : '';
  return (
    <div className={joinClassNames(gridClass, className)} {...rest}>
      {children}
    </div>
  );
}

export function Checkbox({ className, children, ...rest }) {
  return (
    <div className={joinClassNames('checkbox', className)}>
      <label>
        <input type='checkbox' {...rest} />
        {children}
      </label>
    </div>
  );
}

export function InputGroup({ className, children, ...rest }) {
  return (
    <div className={joinClassNames('input-group', className)} {...rest}>
      {children}
    </div>
  );
}

InputGroup.Addon = function InputGroupAddon({ className, children, ...rest }) {
  return (
    <span className={joinClassNames('input-group-addon', className)} {...rest}>
      {children}
    </span>
  );
};

export function Nav({ className, bsStyle, activeKey, children, ...rest }) {
  const styleClass = bsStyle ? `nav-${bsStyle}` : '';
  return (
    <ul className={joinClassNames('nav', styleClass, className)} {...rest}>
      {Children.map(children, (child) => {
        if (!child) {
          return null;
        }
        return cloneElement(child, { __activeKey: activeKey });
      })}
    </ul>
  );
}

export function NavItem({ __activeKey, eventKey, className, onClick, children, ...rest }) {
  const isActive = String(__activeKey) === String(eventKey);
  return (
    <li className={joinClassNames(isActive ? 'active' : '', className)} {...rest}>
      <a
        href='#/'
        onClick={(event) => {
          event.preventDefault();
          if (onClick) {
            onClick(event);
          }
        }}
      >
        {children}
      </a>
    </li>
  );
}

function ModalComponent({ show, onHide, className, children }) {
  useEffect(() => {
    if (!show) {
      return undefined;
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape' && onHide) {
        onHide();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [show, onHide]);

  if (!show) {
    return null;
  }

  return (
    <>
      <div
        className='modal-backdrop fade in'
        onClick={() => {
          if (onHide) {
            onHide();
          }
        }}
      />
      <div className={joinClassNames('modal', 'fade', 'in', className)} style={{ display: 'block' }}>
        <div className='modal-dialog' role='document'>
          <div className='modal-content'>{Children.map(children, (child) => child && cloneElement(child, { __onHide: onHide }))}</div>
        </div>
      </div>
    </>
  );
}

function ModalHeader({ __onHide, closeButton, children, ...rest }) {
  return (
    <div className='modal-header' {...rest}>
      {closeButton && (
        <button
          type='button'
          className='close'
          aria-label='Close'
          onClick={() => {
            if (__onHide) {
              __onHide();
            }
          }}
        >
          <span aria-hidden='true'>&times;</span>
        </button>
      )}
      {children}
    </div>
  );
}

function ModalTitle({ children, ...rest }) {
  return (
    <h4 className='modal-title' {...rest}>
      {children}
    </h4>
  );
}

function ModalBody({ children, ...rest }) {
  return (
    <div className='modal-body' {...rest}>
      {children}
    </div>
  );
}

function ModalFooter({ children, ...rest }) {
  return (
    <div className='modal-footer' {...rest}>
      {children}
    </div>
  );
}

ModalComponent.Header = ModalHeader;
ModalComponent.Title = ModalTitle;
ModalComponent.Body = ModalBody;
ModalComponent.Footer = ModalFooter;

export const Modal = ModalComponent;
